// Feature #283 — Skills Catalogue admin (sm-admin). Create / rename /
// deprecate / merge skills; edit rubric descriptions (labels locked).
//
// Feature #355 — technical skills only. The type chooser on Add, the type
// pill on each row and the second rubric column are gone; the API serves
// and accepts nothing but `technical`.
//
// Bug #357 — the catalogue passed a hundred skills and the page stopped at
// the hundredth. `getSkills` now walks every server page; this page filters
// by name and pages the result client-side, so a long catalogue is
// browsable and the merge picker still sees the whole set.
import React, { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  createSkill,
  getRubric,
  getSkills,
  mergeSkill,
  SmRubricLevel,
  SmSkill,
  updateRubricLevel,
  updateSkill,
} from '@/api/sm'

// Rows per page of the catalogue list. Larger than the platform's 20 for
// list views because rows here are one line and admins scan alphabetically.
const PAGE_SIZE = 50

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const buttonStyle: React.CSSProperties = {
  padding: '6px 14px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryButton: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: 'var(--color-magenta)',
  border: '1px solid var(--color-magenta)',
}

const SkillsCatalogue: React.FC = () => {
  const [skills, setSkills] = useState<SmSkill[]>([])
  const [rubric, setRubric] = useState<SmRubricLevel[]>([])
  const [showInactive, setShowInactive] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [editing, setEditing] = useState<Record<string, { name: string; description: string }>>({})
  const [mergeSource, setMergeSource] = useState<string | null>(null)
  // Rubric edits keyed by `${skill_type}:${value}`. One set arrives since
  // #355, but the key shape is kept so nothing has to change if a second
  // set ever returns.
  const [rubricEdits, setRubricEdits] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  // #357 — name filter and the current page of the filtered list.
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const load = () => {
    getSkills(showInactive).then(setSkills).catch((e) => setError(e?.message))
    getRubric().then(setRubric).catch((e) => setError(e?.message))
  }

  useEffect(load, [showInactive])
  useEffect(() => setPage(1), [query, showInactive])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return skills
    return skills.filter(
      (s) =>
        s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q)
    )
  }, [skills, query])
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const startRecord = visible.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const endRecord = Math.min(currentPage * PAGE_SIZE, visible.length)

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      await fn()
      load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Operation failed')
    }
  }

  const handleCreate = () =>
    run(async () => {
      await createSkill({
        name: newName.trim(),
        skill_type: 'technical',
        description: newDescription.trim() || undefined,
      })
      setNewName('')
      setNewDescription('')
    })

  return (
    <div>
      <PageHeader title="Skills Catalogue" />
      <div style={{ padding: '24px', display: 'grid', gap: '32px', maxWidth: '1100px' }}>
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <section>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Add a skill</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              style={{ ...inputStyle, minWidth: '220px' }}
              placeholder="Skill name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              style={{ ...inputStyle, flex: 1, minWidth: '260px' }}
              placeholder="Description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            <button onClick={handleCreate} disabled={!newName.trim()} style={buttonStyle}>
              Create
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '8px' }}>
            Technical skills should be granular enough to resource against real project needs.
            Nothing here is mandatory on an assessment — people rate the skills they hold.
          </p>
        </section>

        <section>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              Catalogue{' '}
              <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--color-dark-grey)' }}>
                {visible.length === skills.length
                  ? `${skills.length} skill${skills.length === 1 ? '' : 's'}`
                  : `${visible.length} of ${skills.length} skills`}
              </span>
            </h2>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                style={{ ...inputStyle, minWidth: '220px' }}
                placeholder="Find a skill…"
                aria-label="Find a skill"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <label style={{ fontSize: '13px', color: 'var(--color-dark-grey)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  style={{ marginRight: '6px' }}
                />
                Show deprecated
              </label>
            </div>
          </div>
          {visible.length === 0 && skills.length > 0 && (
            <p style={{ fontSize: '14px', color: 'var(--color-dark-grey)' }}>
              No skill matches “{query.trim()}”.
            </p>
          )}
          <div style={{ display: 'grid', gap: '8px' }}>
            {pageItems.map((s) => {
              const edit = editing[s.id]
              return (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    padding: '10px 14px',
                    border: '1px solid var(--color-light-grey)',
                    borderRadius: '8px',
                    opacity: s.is_active ? 1 : 0.55,
                    flexWrap: 'wrap',
                  }}
                >
                  {edit ? (
                    <>
                      <input
                        style={{ ...inputStyle, minWidth: '200px' }}
                        value={edit.name}
                        onChange={(e) =>
                          setEditing((prev) => ({ ...prev, [s.id]: { ...edit, name: e.target.value } }))
                        }
                      />
                      <input
                        style={{ ...inputStyle, flex: 1, minWidth: '200px' }}
                        value={edit.description}
                        placeholder="Description"
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [s.id]: { ...edit, description: e.target.value },
                          }))
                        }
                      />
                      <button
                        style={buttonStyle}
                        onClick={() =>
                          run(async () => {
                            await updateSkill(s.id, {
                              name: edit.name.trim(),
                              description: edit.description.trim() || undefined,
                            })
                            setEditing((prev) => {
                              const next = { ...prev }
                              delete next[s.id]
                              return next
                            })
                          })
                        }
                      >
                        Save
                      </button>
                      <button
                        style={secondaryButton}
                        onClick={() =>
                          setEditing((prev) => {
                            const next = { ...prev }
                            delete next[s.id]
                            return next
                          })
                        }
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontWeight: 500, width: '240px', flexShrink: 0 }}>{s.name}</span>
                      {s.description && (
                        <span style={{ fontSize: '13px', color: 'var(--color-dark-grey)', flex: 1, minWidth: 0 }}>
                          {s.description}
                        </span>
                      )}
                      {!s.is_active && (
                        <span style={{ fontSize: '12px', color: '#92400e' }}>deprecated</span>
                      )}
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button
                          style={secondaryButton}
                          onClick={() =>
                            setEditing((prev) => ({
                              ...prev,
                              [s.id]: { name: s.name, description: s.description || '' },
                            }))
                          }
                        >
                          Edit
                        </button>
                        {s.is_active && (
                          <>
                            <button
                              style={secondaryButton}
                              onClick={() =>
                                run(() => updateSkill(s.id, { is_active: false }))
                              }
                            >
                              Deprecate
                            </button>
                            <button
                              style={secondaryButton}
                              onClick={() => setMergeSource(mergeSource === s.id ? null : s.id)}
                            >
                              {mergeSource === s.id ? 'Cancel merge' : 'Merge into…'}
                            </button>
                          </>
                        )}
                      </span>
                      {mergeSource === s.id && (
                        <select
                          style={{ ...inputStyle, width: '100%' }}
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              const target = e.target.value
                              setMergeSource(null)
                              run(() => mergeSkill(s.id, target))
                            }
                          }}
                        >
                          <option value="">
                            Choose the skill that survives (ratings follow the merge)…
                          </option>
                          {skills
                            .filter((t) => t.id !== s.id && t.is_active)
                            .map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
          {visible.length > PAGE_SIZE && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '12px',
                fontSize: '13px',
                color: 'var(--color-dark-grey)',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span>
                Showing {startRecord} to {endRecord} of {visible.length}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  style={{ ...secondaryButton, opacity: currentPage === 1 ? 0.5 : 1 }}
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  style={{ ...secondaryButton, opacity: currentPage === totalPages ? 0.5 : 1 }}
                  disabled={currentPage === totalPages}
                  onClick={() => setPage(currentPage + 1)}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>

        <section>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>Rubric</h2>
          <p style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
            The five levels every technical skill is rated against. Labels are locked;
            verbose definitions can be edited.
          </p>
          <div style={{ display: 'grid', gap: '8px' }}>
            {rubric
              .filter((level) => level.skill_type === 'technical')
              .map((level) => {
                const key = `${level.skill_type}:${level.value}`
                return (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                      padding: '10px 14px',
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '8px',
                    }}
                  >
                    <strong style={{ width: '110px' }}>
                      {level.value} · {level.label}
                    </strong>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      placeholder="Definition pending"
                      value={rubricEdits[key] ?? level.description ?? ''}
                      onChange={(e) =>
                        setRubricEdits((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    />
                    <button
                      style={buttonStyle}
                      onClick={() =>
                        run(async () => {
                          await updateRubricLevel(
                            level.skill_type,
                            level.value,
                            rubricEdits[key] ?? level.description ?? ''
                          )
                          setRubricEdits((prev) => {
                            const next = { ...prev }
                            delete next[key]
                            return next
                          })
                        })
                      }
                    >
                      Save
                    </button>
                  </div>
                )
              })}
          </div>
        </section>
      </div>
    </div>
  )
}

export default SkillsCatalogue
